import { useEffect, useState } from 'react';
import { AlertOctagon, AlertTriangle, X } from 'lucide-react';
import type { RobotEvent } from '@fabricos/protocol-ts';
import { useFleet } from '@/store/fleet';
import { cn } from '@/lib/utils';
import { formatFlightMode } from '@/lib/flightMode';

interface Toast {
  id: string;
  event: RobotEvent;
  expiresAt: number;
}

const TOAST_DURATION_MS = 6000;

/**
 * AlertHost watches fleet events and renders a top-right stack:
 * - `critical` → sticky cards, dismissed only by Acknowledge
 * - `warning`  → auto-expiring toasts below them
 *
 * NEVER a modal — operator rule (2026-07-14): nothing may block the map or
 * controls, no matter how severe. CriticalAlertFlash supplies the urgency.
 */
export function AlertHost() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [criticalQueue, setCriticalQueue] = useState<RobotEvent[]>([]);

  useEffect(() => {
    const seen = new Set<string>();
    const unsub = useFleet.subscribe((s) => {
      for (const ev of s.events.slice(0, 10)) {
        const key = `${ev.robot_id}-${ev.ts_ms}-${ev.kind}`;
        if (seen.has(key)) continue;
        seen.add(key);
        if (ev.severity === 'warning') {
          setToasts((t) => [
            ...t,
            { id: key, event: ev, expiresAt: Date.now() + TOAST_DURATION_MS },
          ]);
        } else if (ev.severity === 'critical') {
          setCriticalQueue((q) => [...q, ev]);
        }
      }
    });
    return unsub;
  }, []);

  // Expire toasts.
  useEffect(() => {
    const i = setInterval(() => {
      setToasts((t) => t.filter((x) => x.expiresAt > Date.now()));
    }, 500);
    return () => clearInterval(i);
  }, []);

  return (
    <div className="pointer-events-none fixed top-14 right-4 z-50 flex flex-col gap-2">
      {criticalQueue.slice(0, 3).map((ev, i) => (
        <CriticalCard
          key={`${ev.robot_id}-${ev.ts_ms}-${ev.kind}`}
          event={ev}
          onAck={() => setCriticalQueue((q) => q.filter((_, j) => j !== i))}
        />
      ))}
      {criticalQueue.length > 3 && (
        <p className="self-end rounded bg-surface-2/95 px-2 py-0.5 text-[11px] text-status-critical">
          +{criticalQueue.length - 3} more critical
        </p>
      )}
      {toasts.map((t) => (
        <ToastCard
          key={t.id}
          event={t.event}
          onDismiss={() => setToasts((arr) => arr.filter((x) => x.id !== t.id))}
        />
      ))}
    </div>
  );
}

function ToastCard({ event, onDismiss }: { event: RobotEvent; onDismiss: () => void }) {
  return (
    <div className="pointer-events-auto flex w-80 items-start gap-2 rounded-lg border border-status-warn/40 bg-surface-2/95 p-3 shadow-2xl backdrop-blur-md">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-warn" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-status-warn">{formatKind(event)}</p>
        <p className="mt-0.5 truncate text-xs text-fg-secondary">{event.robot_id}</p>
        <p className="mt-1 text-xs text-fg-muted">{eventDetail(event)}</p>
      </div>
      <button onClick={onDismiss} className="text-fg-muted hover:text-fg-primary">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Sticky (no auto-expire) corner card for critical events. Same footprint
 *  as a toast so it never covers the map or controls. */
function CriticalCard({ event, onAck }: { event: RobotEvent; onAck: () => void }) {
  return (
    <div className="pointer-events-auto flex w-80 items-start gap-2 rounded-lg border border-status-critical bg-surface-1/95 p-3 shadow-2xl backdrop-blur-md">
      <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0 text-status-critical" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold text-status-critical">{formatKind(event)}</p>
        <p className="mt-0.5 truncate font-mono text-xs text-fg-secondary">{event.robot_id}</p>
        <p className="mt-1 text-xs text-fg-muted">{eventDetail(event)}</p>
      </div>
      <button
        onClick={onAck}
        className={cn(
          'rounded bg-status-critical px-2 py-1 text-[11px] font-semibold text-white',
          'hover:bg-status-critical/90',
        )}
      >
        Ack
      </button>
    </div>
  );
}

function formatKind(e: RobotEvent): string {
  switch (e.kind) {
    case 'battery_low': return 'Battery low';
    case 'battery_critical': return 'Battery CRITICAL';
    case 'link_lost': return 'Link lost';
    case 'link_restored': return 'Link restored';
    case 'gps_fix_changed': return 'GPS fix changed';
    case 'failsafe_triggered': return 'Failsafe triggered';
    case 'error': return 'Error';
    case 'arm_state_changed': return e.armed ? 'Armed' : 'Disarmed';
    case 'mode_changed': return `Mode → ${formatFlightMode(e.mode)}`;
    case 'waypoint_reached': return `Waypoint ${e.seq} reached`;
    case 'mission_complete': return 'Mission complete';
    case 'takeoff_complete': return 'Takeoff complete';
    case 'landed': return 'Landed';
    case 'connected': return 'Connected';
    case 'disconnected': return 'Disconnected';
    case 'text': return 'Vehicle message';
    default: return String((e as { kind: string }).kind);
  }
}

function eventDetail(e: RobotEvent): string {
  switch (e.kind) {
    case 'battery_low':
    case 'battery_critical':
      return `${e.remaining_pct.toFixed(0)}% remaining`;
    case 'failsafe_triggered':
      return e.reason;
    case 'error':
      return `${e.code}: ${e.message}`;
    case 'text':
      return e.message;
    case 'gps_fix_changed':
      return `${e.fix} · ${e.satellites} sats`;
    default:
      return '';
  }
}
